import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useSchedulingLocationInfo } from '../../../hooks/use-scheduling-location-info';
import { StatusBadgeVariants } from '../../status-badge';
import {
  activitySourceFilterOptions,
  activitySourceFilterToTypes,
  activityTypeLabels,
  statusTypeBadgeVariants,
} from '../constants';
import { DEFAULT_FILTERS, FILTER_VALUE_DELIMITER } from './constants';
import { filterAppointmentTypeIdsByAvailability } from './helpers';
import type { FilterOptions, FilterState } from './types';
import { useScheduleActivityFiltersShallowStore } from './use-schedule-activity-filters-store';

export const useScheduleActivityFilters = (locationId: string) => {
  const { selectedOrgId } = useSchedulingLocationInfo();

  const { data: appointmentTypesData } = SchedulerV3.Helpers.useGetAppointmentTypesV3Data({
    selectedLocationId: locationId,
    selectedLocationIds: [locationId],
    pageConfig: { page: 1, limit: SchedulerV3.Constants.CALENDAR_VIEW_V3_API_QUERY_LIMIT },
  });

  const { filtersMap, setFilters, resetFilters } = useScheduleActivityFiltersShallowStore(
    'filtersMap',
    'setFilters',
    'resetFilters'
  );

  const filtersKey = selectedOrgId || locationId;

  const filters = filtersMap[filtersKey] ?? DEFAULT_FILTERS;

  const filterOptions: FilterOptions = useMemo(() => {
    const activityTypes = Object.entries(activityTypeLabels).map(([value, label]) => ({ value, label }));

    const appointmentTypes = (appointmentTypesData?.appointmentTypes ?? [])
      .filter((apptType) => apptType.id && apptType.displayName)
      .map((apptType) => ({ value: apptType.id, label: apptType.displayName || '' }))
      .sort((a, b) => a.label.localeCompare(b.label));

    // The option value carries every underlying ActivitySourceType behind the filter category,
    // joined by the delimiter, so selecting it filters by all of them.
    const sourceTypes = Object.entries(activitySourceFilterOptions).map(([key, label]) => ({
      value: activitySourceFilterToTypes[key as keyof typeof activitySourceFilterToTypes].join(FILTER_VALUE_DELIMITER),
      label,
    }));

    // Group statuses by the badge they render, so each StatusBadge appears once.
    // The option value carries every status behind that badge, joined by the delimiter, so selecting it filters by all of them.
    const statusTypes = Object.entries(statusTypeBadgeVariants).reduce<{ value: string; label: StatusBadgeVariants }[]>(
      (acc, [status, variant]) => {
        const existing = acc.find((item) => item.label === variant);
        if (existing) {
          existing.value += `${FILTER_VALUE_DELIMITER}${status}`;
        } else {
          acc.push({ value: status, label: variant });
        }
        return acc;
      },
      []
    );

    return {
      activityTypes,
      appointmentTypes,
      sourceTypes,
      statusTypes,
    };
  }, [appointmentTypesData?.appointmentTypes]);

  const availableAppointmentTypeIds = useMemo(
    () => filterOptions.appointmentTypes.map(({ value }) => value),
    [filterOptions.appointmentTypes]
  );
  const appointmentTypeIds = useMemo(
    () => filterAppointmentTypeIdsByAvailability(filters.appointmentTypeIds, availableAppointmentTypeIds),
    [availableAppointmentTypeIds, filters.appointmentTypeIds]
  );
  const hasInvalidAppointmentTypeIds = appointmentTypeIds.length !== filters.appointmentTypeIds.length;

  // Remove any appointment type ids from the filters that are no longer available for the current selected location.
  useEffect(() => {
    if (!appointmentTypesData || !hasInvalidAppointmentTypeIds) return;

    setFilters(filtersKey, (currentFilters) => {
      const nextAppointmentTypeIds = filterAppointmentTypeIdsByAvailability(
        currentFilters.appointmentTypeIds,
        availableAppointmentTypeIds
      );

      if (nextAppointmentTypeIds.length === currentFilters.appointmentTypeIds.length) return currentFilters;

      return {
        ...currentFilters,
        appointmentTypeIds: nextAppointmentTypeIds,
      };
    });
  }, [appointmentTypesData, availableAppointmentTypeIds, filtersKey, hasInvalidAppointmentTypeIds, setFilters]);

  const handleFiltersChange: Dispatch<SetStateAction<FilterState>> = (updatedFilters) => {
    setFilters(filtersKey, updatedFilters);
  };

  return {
    filters: {
      ...filters,
      appointmentTypeIds,
    },
    filterOptions,
    setFilters: handleFiltersChange,
    resetFilters: () => resetFilters(filtersKey),
  };
};
